import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

const HEADING_ID = "submitting-overlay-heading";

/**
 * Full-screen overlay shown while a form submit is in flight. The submit fans out
 * across several Salesforce objects (Contract, Account, Contacts, contributions,
 * affiliation/ownership) so it can take a few seconds — this reassures the user
 * that work is happening and blocks interaction (preventing a double-submit)
 * beyond just disabling the button.
 */
export function SubmittingOverlay({ updating = false }: { updating?: boolean }) {
  const verb = updating ? "Updating" : "Submitting";
  const dialogRef = useRef<HTMLDivElement>(null);

  // Pull focus into the modal on mount so AT users land here (without it, focus stays
  // on the Submit button under the overlay and the dialog isn't really modal for them).
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={HEADING_ID}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6 outline-none"
    >
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-8 shadow-lg text-center">
        <div className="flex items-center justify-center text-brand-green mb-4" aria-hidden="true">
          <Loader2 size={36} className="animate-spin" />
        </div>
        <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2" aria-live="polite">
          {verb} your form
        </h1>
        <p className="text-gray-600">
          Please don't close or refresh this page - this may take a moment
        </p>
      </div>
    </div>
  );
}
